import { useCallback, useEffect, useState } from 'react'
import { api, ApiError, setToken } from './api'
import { Logo } from './components/Logo'
import { Badge, Empty, Modal, Notice, StatTile, Table, TileRow } from './components/ui'
import type { HealthDetail, Role, Session } from './types'

import Dashboard from './pages/Dashboard'
import Campaigns from './pages/Campaigns'
import Leads from './pages/Leads'
import Dialer from './pages/Dialer'
import Agents from './pages/Agents'
import Calls from './pages/Calls'
import Pacing from './pages/Pacing'
import Reports from './pages/Reports'
import Settings from './pages/Settings'
import Compliance from './pages/Compliance'
import Voicemail from './pages/Voicemail'

type Route =
  | 'dashboard'
  | 'campaigns'
  | 'leads'
  | 'dialer'
  | 'agents'
  | 'calls'
  | 'reports'
  | 'pacing'
  | 'voicemail'
  | 'compliance'
  | 'settings'

interface NavItem {
  route: Route
  label: string
  icon: string
  /** Minimum role. Agents see only the station they work in. */
  min: Role
}

const NAV: NavItem[] = [
  { route: 'dashboard', label: 'Dashboard', icon: 'grid', min: 'supervisor' },
  { route: 'campaigns', label: 'Campaigns', icon: 'target', min: 'supervisor' },
  { route: 'leads', label: 'Leads', icon: 'list', min: 'supervisor' },
  { route: 'dialer', label: 'Dialer', icon: 'phone', min: 'agent' },
  { route: 'agents', label: 'Agents', icon: 'users', min: 'supervisor' },
  { route: 'calls', label: 'Calls', icon: 'log', min: 'supervisor' },
  { route: 'reports', label: 'Reports', icon: 'chart', min: 'supervisor' },
  { route: 'pacing', label: 'Pacing', icon: 'gauge', min: 'supervisor' },
  { route: 'voicemail', label: 'Voicemail', icon: 'phone', min: 'supervisor' },
  { route: 'compliance', label: 'Compliance', icon: 'shield', min: 'supervisor' },
  { route: 'settings', label: 'Settings', icon: 'cog', min: 'admin' },
]

const RANK: Record<Role, number> = { agent: 0, supervisor: 1, admin: 2 }

function allowed(item: NavItem, role: Role): boolean {
  return RANK[role] >= RANK[item.min]
}

export default function App() {
  const [session, setSession] = useState<Session | null>(null)

  if (!session) return <SignIn onSignedIn={setSession} />
  return <Shell session={session} onSignOut={() => setSession(null)} />
}

// ================================================================ sign in ====

function SignIn({ onSignedIn }: { onSignedIn: (s: Session) => void }) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const s = await api.login(username, password)
      setToken(s.token)
      onSignedIn(s)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not sign in')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="signin">
      <div className="signin-art" aria-hidden="true">
        <div className="signin-glow" />
      </div>
      <form className="signin-card" onSubmit={submit}>
        <div className="brand">
          <Logo size={26} />
          <div>
            <h1>Impact Dialer</h1>
            <p>Contact centre platform</p>
          </div>
        </div>

        <label>
          Username
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            autoFocus
          />
        </label>
        <label>
          Password
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
          />
        </label>

        {error && <p className="error">{error}</p>}

        <button className="primary" type="submit" disabled={busy || !username || !password}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>

        {/* Studio credit. Plain text rather than a link: this is a sign-in
            screen, and the one thing it should not do is invite a click away
            from it. The linked version lives on Settings → System, where an
            admin might actually want it. */}
        <p className="signin-foot">
          A <strong>PinkViper Labs</strong> product
        </p>
      </form>
    </div>
  )
}

// ================================================================== shell ====

function Shell({ session, onSignOut }: { session: Session; onSignOut: () => void }) {
  const nav = NAV.filter((n) => allowed(n, session.role))
  const fallback: Route = nav[0]?.route ?? 'dialer'

  // Hash routing rather than a router dependency. A console benefits from
  // linkable screens ("look at /#calls"), and this is the whole of what a
  // router would provide here.
  const [route, setRoute] = useState<Route>(() => routeFromHash(nav, fallback))

  useEffect(() => {
    const onHash = () => setRoute(routeFromHash(nav, fallback))
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [nav, fallback])

  const go = useCallback((r: Route) => {
    location.hash = r
  }, [])

  const [health, setHealth] = useState<{ database: boolean; media: boolean } | null>(null)
  const [inspecting, setInspecting] = useState(false)
  useEffect(() => {
    const poll = () => api.health().then(setHealth).catch(() => setHealth(null))
    poll()
    const t = setInterval(poll, 30000)
    return () => clearInterval(t)
  }, [])

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand brand-sm">
          <Logo size={26} />
          <strong>Impact Dialer</strong>
        </div>

        <nav>
          {nav.map((item) => (
            <button
              key={item.route}
              className={route === item.route ? 'nav-item active' : 'nav-item'}
              onClick={() => go(item.route)}
            >
              <Icon name={item.icon} />
              {item.label}
            </button>
          ))}
        </nav>

        <div className="sidebar-foot">
          {/* The indicators are a button, not decoration: "media is down" is
              the start of a question, and the answer lives one click away
              rather than in a log the operator has to SSH for. */}
          <button
            className="sys-health"
            onClick={() => setInspecting(true)}
            title="Inspect system health"
          >
            <HealthDot ok={!!health?.database} label="Database" />
            <HealthDot ok={!!health?.media} label="Media" />
          </button>
          <div className="who">
            <div className="who-name">{session.full_name}</div>
            <div className="who-role">{session.role}</div>
          </div>
          <button className="ghost" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      </aside>

      {inspecting && <HealthInspector onClose={() => setInspecting(false)} />}

      <main className="content">
        {route === 'dashboard' && <Dashboard onNavigate={go} />}
        {route === 'campaigns' && <Campaigns />}
        {route === 'leads' && <Leads />}
        {route === 'dialer' && <Dialer session={session} />}
        {route === 'agents' && <Agents role={session.role} userId={session.user_id} />}
        {route === 'calls' && <Calls />}
        {route === 'reports' && <Reports />}
        {route === 'pacing' && <Pacing />}
        {route === 'voicemail' && <Voicemail />}
        {route === 'compliance' && <Compliance role={session.role} />}
        {route === 'settings' && <Settings />}
      </main>
    </div>
  )
}

function routeFromHash(nav: NavItem[], fallback: Route): Route {
  const h = location.hash.replace('#', '') as Route
  return nav.some((n) => n.route === h) ? h : fallback
}

function HealthDot({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span className={`health ${ok ? 'up' : 'down'}`} title={`${label}: ${ok ? 'up' : 'down'}`}>
      <span className="health-dot" />
      {label}
    </span>
  )
}

function Icon({ name }: { name: string }) {
  // Inline paths rather than an icon dependency: eight glyphs do not justify a
  // package, and these stay crisp at the one size the sidebar uses.
  const paths: Record<string, string> = {
    grid: 'M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z',
    target: 'M12 2a10 10 0 100 20 10 10 0 000-20zm0 5a5 5 0 100 10 5 5 0 000-10zm0 4a1 1 0 100 2 1 1 0 000-2z',
    list: 'M4 6h16M4 12h16M4 18h10',
    phone: 'M6 3h4l2 5-3 2a12 12 0 005 5l2-3 5 2v4a2 2 0 01-2 2A17 17 0 014 5a2 2 0 012-2z',
    users: 'M9 11a4 4 0 100-8 4 4 0 000 8zm8 10v-2a5 5 0 00-5-5H6a5 5 0 00-5 5v2M18 8h5M20.5 5.5v5',
    log: 'M5 3h11l4 4v14H5zM16 3v5h5M8 13h8M8 17h5',
    chart: 'M4 20V10M10 20V4M16 20v-7M22 20H2',
    gauge: 'M12 14a2 2 0 100-4 2 2 0 000 4zM13.4 10.6L19 5M3.5 19a10 10 0 1117 0',
    shield: 'M12 2l8 4v6c0 5-3.4 8.9-8 10-4.6-1.1-8-5-8-10V6zM9 12l2 2 4-4',
    cog: 'M12 15a3 3 0 100-6 3 3 0 000 6zM19.4 15a1.7 1.7 0 00.3 1.9l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.9-.3 1.7 1.7 0 00-1 1.5V21a2 2 0 11-4 0v-.1A1.7 1.7 0 008.9 19a1.7 1.7 0 00-1.9.3l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.7 1.7 0 00.3-1.9 1.7 1.7 0 00-1.5-1H3a2 2 0 110-4h.1A1.7 1.7 0 005 8.9a1.7 1.7 0 00-.3-1.9l-.1-.1a2 2 0 112.8-2.8l.1.1a1.7 1.7 0 001.9.3H9.5a1.7 1.7 0 001-1.5V3a2 2 0 114 0v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.9-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.7 1.7 0 00-.3 1.9v.1a1.7 1.7 0 001.5 1H21a2 2 0 110 4h-.1a1.7 1.7 0 00-1.5 1z',
  }
  return (
    <svg viewBox="0 0 24 24" className="icon" fill="none" stroke="currentColor" strokeWidth="1.8"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={paths[name] ?? ''} />
    </svg>
  )
}

/**
 * System health, in detail.
 *
 * Every value here is measured by the backend at request time. Nothing is
 * inferred and nothing is padded out to look thorough: where a fact cannot be
 * obtained — a trunk that does not register, so has no registration state —
 * the panel says so rather than showing a reassuring green tick.
 */
function HealthInspector({ onClose }: { onClose: () => void }) {
  const [data, setData] = useState<HealthDetail | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const poll = () =>
      api
        .healthDetail()
        .then((d) => {
          setData(d)
          setError(null)
        })
        .catch((e) => setError(e instanceof ApiError ? e.message : 'Could not read health'))
    poll()
    const t = setInterval(poll, 5000)
    return () => clearInterval(t)
  }, [])

  const drift = data ? data.dialer.tracked_channels - data.dialer.asterisk_channels : 0

  return (
    <Modal title="System health" onClose={onClose} wide>
      {error && <Notice kind="error">{error}</Notice>}
      {!data ? (
        <p className="muted">Reading…</p>
      ) : (
        <>
          <TileRow>
            <StatTile
              label="Database"
              value={data.database.connected ? 'up' : 'down'}
              sub={`${data.database.latency_ms} ms · ${data.database.pool_idle}/${data.database.pool_connections} idle`}
              tone={data.database.connected ? 'ok' : 'danger'}
            />
            <StatTile
              label="Media server"
              value={data.media.connected ? 'up' : 'down'}
              sub={`${data.media.latency_ms} ms ARI`}
              tone={data.media.connected ? 'ok' : 'danger'}
            />
            <StatTile
              label="Endpoints online"
              value={`${data.media.endpoints_online}/${data.media.endpoints_total}`}
              sub="registered with Asterisk"
            />
            <StatTile label="Active channels" value={data.media.active_channels} tone="live" />
            <StatTile label="Active bridges" value={data.media.active_bridges} />
            <StatTile label="Agents online" value={data.dialer.agents_online} />
          </TileRow>

          {data.media.asterisk_version && (
            <p className="muted small">
              Asterisk <span className="mono">{data.media.asterisk_version}</span> · Impact Dialer{' '}
              <span className="mono">{data.version}</span>
            </p>
          )}

          {drift !== 0 && (
            <Notice kind="warn">
              The dialer is tracking {data.dialer.tracked_channels} channels but Asterisk holds{' '}
              {data.dialer.asterisk_channels}. A gap that does not close on its own means
              reconciliation is not converging.
            </Notice>
          )}

          <h3 className="form-section">Registered endpoints</h3>
          {data.media.registered.length === 0 ? (
            <Empty
              message="No endpoints are registered."
              hint="Agents cannot receive their leg until their phone registers."
            />
          ) : (
            <Table head={['Endpoint', 'Technology', 'Channels']}>
              {data.media.registered.map((e) => (
                <tr key={e.resource}>
                  <td className="mono strong">{e.resource}</td>
                  <td className="muted">{e.technology}</td>
                  <td>{e.channels}</td>
                </tr>
              ))}
            </Table>
          )}

          <h3 className="form-section">Trunks</h3>
          {data.trunks.length === 0 ? (
            <Empty message="No trunks configured." hint="No outbound call can be placed yet." />
          ) : (
            <Table head={['Trunk', 'Host', 'Enabled', 'Known to Asterisk', 'State']}>
              {data.trunks.map((t) => (
                <tr key={t.name} className={t.configured_in_asterisk ? '' : 'row-danger'}>
                  <td className="strong">{t.name}</td>
                  <td className="mono muted">{t.host}</td>
                  <td>{t.active ? 'yes' : 'no'}</td>
                  <td>
                    {t.configured_in_asterisk ? (
                      <Badge tone="ok">yes</Badge>
                    ) : (
                      <Badge tone="danger">missing from pjsip.conf</Badge>
                    )}
                  </td>
                  <td className="muted">{t.asterisk_state ?? '—'}</td>
                </tr>
              ))}
            </Table>
          )}
        </>
      )}
      <div className="modal-actions">
        <button className="primary" onClick={onClose}>
          Close
        </button>
      </div>
    </Modal>
  )
}
